import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, RouterEvent } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UtilityService } from './services/utility.service';
import { filter } from 'rxjs/operators';
import { LocationStrategy } from '@angular/common';
import { AcceptUpdatedTermsComponent } from './components/modules/terms/accept-updated-terms/accept-updated-terms.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from './services/api.service';
import { ThemeService } from './services/theme.service';
import { SessionService } from './services/session.service';
import { SessionTimeoutWarningComponent } from './components/_shared/modals/session-timeout-warning/session-timeout-warning.component';
import { VersionCheckService } from './services/version-check.service';
import { Subscription } from 'rxjs';
import { FeatureFlagService } from './services/feature-flag.service';
import { ReleaseNotesService } from './services/release-notes.service';
import { ReleaseNotesPopupComponent } from './components/_shared/support/release-notes-popup/release-notes-popup.component';
import { PostLoginHospitalPopupsService } from './services/post-login-hospital-popups.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
  title = 'cloud-animal-health';
  showSpin: boolean = false
  subscription: Subscription | undefined;
  sessionSubscription: Subscription | undefined;
  sessionWarningDialog: MatDialogRef<SessionTimeoutWarningComponent> | null = null;
  termsDialogRef: MatDialogRef<AcceptUpdatedTermsComponent> | null = null;
  termsCheckInProgress = false;
  releaseNotesDialogRef: MatDialogRef<ReleaseNotesPopupComponent> | null = null;
  someNavigationService: any;
  beforeLoginPaths = [
    // These paths don't require terms acceptance check
    '/auth'
  ]

  constructor(
    private utility : UtilityService,
    private cdr: ChangeDetectorRef,
    private location: LocationStrategy,
    private router: Router,
    public dialog: MatDialog,
    private api: ApiService,
    private themeService: ThemeService,
    private titleService: Title,
    private sessionService: SessionService,
    private versionCheck: VersionCheckService,
    private featureFlagService: FeatureFlagService,
    private releaseNotesService: ReleaseNotesService,
    private postLoginHospitalPopupsService: PostLoginHospitalPopupsService
  ) {
    this.location.onPopState(() => {
      // set isBackButtonClicked to true.
      // this.someNavigationService.setBackClicked(true);
      // return false;
    });
  //   navigator.mediaDevices.getUserMedia({video: true}).then(function(stream){
  //     console.log("stream.getTracks(): ", stream.getTracks());
      
  //      stream.getTracks().forEach(function (track) {
  //        track.stop()
  //             });
     
  //  })
  //   navigator.mediaDevices.getUserMedia({video: true}).then(function(stream){
  //     let result = stream.getVideoTracks().some(function (track) {
  //                 return track.enabled && track.readyState === 'live';
  //             });
  //     if(result){
  //         alert("On");
  //     }else{
  //         alert("Off");
  //     }
  // }).catch(function(err) { console.log("65================",err.name + ": " + err.message); }); 
  }
  ngOnInit() {
    // Initialize theme based on environment configuration
    this.themeService.initializeTheme();

    // Update page title based on theme
    this.titleService.setTitle(this.themeService.getDisplayName());

    // Initialize version checking for new deployments
    this.versionCheck.initVersionCheck();

    this.subscription = this.utility.showSpinner
    .subscribe(res => {
      this.showSpin = res
      this.cdr.detectChanges()
    })

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)

    ).subscribe(() => {
      const currentUrl = this.router.url

      const userType = localStorage.getItem("endUserType")
      const userId = localStorage.getItem("endUserId")
      // Skip terms check for admin always, and for specialist when specialty intake is enabled
      const skipTerms = userType === 'admin' ||
        (userType === 'specialist' && this.featureFlagService.isSpecialtyIntakeEnabled());
      const requiresTermsCheck = this.beforeLoginPaths.indexOf(currentUrl) === -1 && userType && userId && !skipTerms;

      if (this.beforeLoginPaths.indexOf(currentUrl) === -1 && userType && userId && ['pcv', 'vc', 'specialist'].includes(userType)) {
        if (requiresTermsCheck) {
          this.checkIfTermsAccepted(userId);
        } else {
          this.handlePostTermsChecks(userId);
        }
      }
    });

    // Initialize session timeout monitoring (MPH only)
    this.initializeSessionMonitoring();

    // Restore session if user is already logged in
    this.restoreSessionIfLoggedIn();
  }

  /**
   * Restore session monitoring if user is already logged in (e.g., after page refresh)
   */
  restoreSessionIfLoggedIn(): void {
    if (!this.sessionService.isSessionTimeoutEnabled) {
      return;
    }

    // Check if user is logged in by checking localStorage
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('endUserId');

    if (accessToken && userId) {
      // User is logged in - check if there's existing session info
      try {
        const sessionInfoStr = localStorage.getItem('mph_session_info');
        if (sessionInfoStr) {
          const sessionInfo = JSON.parse(sessionInfoStr);

          // Check if session has expired
          const now = new Date();
          const expiry = new Date(sessionInfo.absoluteExpiry);

          if (now >= expiry) {
            // Session has expired - logout
            console.log('[Session] Session expired on page load - logging out');
            this.api.logout();
          } else {
            // Session still valid - restart monitoring
            console.log('[Session] Restoring session monitoring after page refresh');
            this.sessionService.startSession(sessionInfo.rememberMe || false);
          }
        } else {
          // No session info found but user is logged in
          // This might happen if user logged in before the session feature was added
          // Start a new session with default settings
          console.log('[Session] No session info found - starting new session');
          this.sessionService.startSession(false);
        }
      } catch (error) {
        console.error('[Session] Error restoring session:', error);
        // Start a new session as fallback
        this.sessionService.startSession(false);
      }
    }
  }

  /**
   * Initialize session timeout monitoring for MPH environment
   */
  initializeSessionMonitoring(): void {
    if (!this.sessionService.isSessionTimeoutEnabled) {
      return;
    }

    // Subscribe to session state changes
    this.sessionSubscription = this.sessionService.sessionStateChanges.subscribe(state => {
      if (state.isIdle && !state.isTimedOut) {
        // User is idle - show warning modal if not already shown
        if (!this.sessionWarningDialog) {
          this.showSessionWarningModal(state.countdown);
        } else {
          // Update countdown in existing modal
          this.sessionWarningDialog.componentInstance.countdown = state.countdown;
        }
      } else if (!state.isIdle) {
        // User became active again - close warning modal
        if (this.sessionWarningDialog) {
          this.sessionWarningDialog.close();
          this.sessionWarningDialog = null;
        }
      } else if (state.isTimedOut) {
        // Session timed out - close modal if open
        if (this.sessionWarningDialog) {
          this.sessionWarningDialog.close();
          this.sessionWarningDialog = null;
        }
      }
    });
  }

  /**
   * Show session timeout warning modal
   */
  showSessionWarningModal(countdown: number): void {
    this.sessionWarningDialog = this.dialog.open(SessionTimeoutWarningComponent, {
      width: '500px',
      disableClose: true,
      data: { countdown }
    });

    this.sessionWarningDialog.afterClosed().subscribe(result => {
      if (result === 'continue') {
        // User wants to continue session
        this.sessionService.continueSession();
      } else if (result === 'logout') {
        // User wants to logout
        this.api.logout();
      }
      this.sessionWarningDialog = null;
    });
  }

  checkIfTermsAccepted(userId) {
    // Prevent showing multiple terms modals if one is already open
    if (this.termsDialogRef || this.termsCheckInProgress) {
      console.log('[Terms] Modal already open, skipping duplicate check');
      return;
    }

    this.termsCheckInProgress = true;
    this.api.apiRequest("get", `user/${userId}` , {})
    .subscribe(res=>{
      this.termsCheckInProgress = false;
      let user = res?.data.user
      if(user && user.isTermsAccepted === false) {
        // Only show modal if not already shown
        if (!this.termsDialogRef) {
          this.termsDialogRef = this.dialog.open(
            AcceptUpdatedTermsComponent, {disableClose: true});

          this.termsDialogRef.afterClosed().subscribe(response => {
            this.termsDialogRef = null; // Clear reference when modal closes

            if (response) {
              if(response === 'yes') {
                this.acceptTerms(userId)
              } else {
                this.api.logout()
              }
            }
          });
        }
      } else {
        this.handlePostTermsChecks(userId);
      }
    }, () => {
      this.termsCheckInProgress = false;
    })
  }

  acceptTerms(userId) {
    const reqVars = {isTermsAccepted: true}
    this.api.apiRequest("put", `user/${userId}/terms` , reqVars)
    .subscribe(res=>{
      this.handlePostTermsChecks(userId);
    })
  }

  /**
   * Once terms have been confirmed as accepted (or don't need checking),
   * route to the appropriate post-login pop-up flow:
   * - pcv/vc hospital users: the combined, layered hospital pop-up stack
   *   (site selection / hospital group email / release notes), gated to
   *   their post-login landing route.
   * - specialists (and any other eligible user type): the existing
   *   standalone release-notes popup, unchanged.
   */
  private handlePostTermsChecks(userId: string): void {
    const userType = localStorage.getItem('endUserType');
    if (userType === 'pcv' || userType === 'vc') {
      if (localStorage.getItem('endUserId') === userId && this.isOnPcvVcPopupLandingRoute()) {
        this.postLoginHospitalPopupsService.checkAndShowPopups(userId);
      }
    } else {
      this.checkUnreadReleaseNotes(userId);
    }
  }

  /**
   * pcv/vc's post-login landing route differs by mode: when specialty
   * intake is enabled, sign-in always routes to `/pcv/view-cases` (the
   * `dashboard` sidebar item and route are not used at all in that mode —
   * see `PcvComponent.ngOnInit` filtering it out and `pcv-sign-in.component.ts`'s
   * redirect), so the combined hospital pop-up stack must be gated there
   * instead of the legacy `/pcv/dashboard` route used by
   * `isOnDashboardRoute()`. Outside specialty intake, none of the three
   * pop-ups' preconditions apply except release notes, so fall back to the
   * existing dashboard-route gating for parity with pre-existing behavior.
   */
  private isOnPcvVcPopupLandingRoute(): boolean {
    const path = this.router.url.split('?')[0];
    if (this.featureFlagService.isSpecialtyIntakeEnabled()) {
      return path === '/pcv/view-cases';
    }
    return this.isOnDashboardRoute();
  }

  checkUnreadReleaseNotes(userId: string): void {
    if (localStorage.getItem('endUserId') !== userId || this.termsDialogRef || this.releaseNotesDialogRef || !this.isOnDashboardRoute()) {
      return;
    }

    this.releaseNotesService.getUnreadReleaseNotes().subscribe(data => {
      if (this.releaseNotesDialogRef) {
        return;
      }

      const count = data.count || 0;
      if (!count) {
        return;
      }

      // Only re-show the popup this session if it hasn't been shown yet, or if
      // more notes have become unread since it was last shown (e.g. a new
      // feature was published after the user dismissed it).
      const lastShownCount = this.getReleaseNotesPopupShownCount(userId);
      if (lastShownCount !== null && count <= lastShownCount) {
        return;
      }

      this.releaseNotesDialogRef = this.dialog.open(ReleaseNotesPopupComponent, {
        width: '560px',
        maxWidth: '92vw',
        data: { releaseNotes: data.releaseNotes }
      });
      this.setReleaseNotesPopupShownCount(userId, count);

      this.releaseNotesDialogRef.afterClosed().subscribe(() => {
        this.releaseNotesDialogRef = null;
      });
    });
  }

  /**
   * The release-notes login popup should only be considered for display while
   * the user is on their dashboard route, not on arbitrary authenticated pages.
   */
  private isOnDashboardRoute(): boolean {
    const path = this.router.url.split('?')[0];
    const userType = localStorage.getItem('endUserType');

    // 'day2' renders the same dashboard main component as '' (see
    // specialist-dashboard-routing.module.ts / pcv-dashboard-routing.module.ts) —
    // it's the actual post-login landing route for active users, not a
    // separate page. Other dashboard-prefixed routes (invite, add-services,
    // appointement-detail-done) are distinct pages nested there only for
    // lazy-loading and are not "the dashboard".
    if (userType === 'specialist') {
      return path === '/specialist/dashboard' || path === '/specialist/dashboard/day2';
    }

    if (userType === 'pcv' || userType === 'vc') {
      return path === '/pcv/dashboard' || path === '/pcv/dashboard/day2';
    }

    return false;
  }

  private releaseNotesPopupCountStorageKey(userId: string): string {
    return `releaseNotesPopupShownCount_${userId}`;
  }

  /**
   * The unread count recorded in sessionStorage the last time the popup was
   * shown for this user in the current browser session, or null if it hasn't
   * been shown yet this session.
   */
  private getReleaseNotesPopupShownCount(userId: string): number | null {
    const raw = sessionStorage.getItem(this.releaseNotesPopupCountStorageKey(userId));
    return raw !== null ? parseInt(raw, 10) : null;
  }

  private setReleaseNotesPopupShownCount(userId: string, count: number): void {
    sessionStorage.setItem(this.releaseNotesPopupCountStorageKey(userId), String(count));
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
    }

    // Close session warning modal if open
    if (this.sessionWarningDialog) {
      this.sessionWarningDialog.close();
    }

    if (this.releaseNotesDialogRef) {
      this.releaseNotesDialogRef.close();
    }
  }
}
